'use strict';

/**
 * Validation rules — "has this question been answered enough to proceed?"
 *
 * Data-only ports of two legacy jQuery helpers that decided the same question
 * from the rendered page:
 *
 *   - `helpers.js` `isRequiredSatisfied` — every visible row answered (grid, and
 *     the Base default).
 *   - `single-multiple/displaymax.js` `requiredIsSatisfied` — on the last page,
 *     at least one option chosen (SingleMultiple).
 *
 * Both counted rows off the DOM via `base/displaymax.js` `getSelectedRowsCount`.
 * These rules reproduce that count from the question object and an answers
 * snapshot, so validation survives a rendering change and never disagrees with
 * submission, which already reads the store (RPI-880). See `../answers-adapter`
 * for how a widget builds the snapshot and `./README.md` for the contract.
 */

/**
 * Is a stored value a selection? Presence, not identity: neither `undefined`
 * (unwritten) nor `''` (written while empty). The value is opaque — a checked
 * box stores `'on'`, a radio/dropdown its code, an open answer its text — so it
 * is never compared to a response code. See `on-vs-code` in `../answers-adapter`.
 */
function isSelected(answers, name) {
  if (typeof name !== 'string') {
    return false;
  }
  var value = answers[name];
  return value !== undefined && value !== '';
}

/**
 * The rows shown on the current page. `question.phases` is the `displaymax`
 * chunking the backend sends (grid rows / SingleMultiple options), and
 * `phases[phase_pos]` is the current chunk — what `visible_rows_count`
 * measured. An out-of-range `phase_pos` reads as no rows (an empty
 * `#phase-{pos}`) rather than falling back to every row. A non-paginated
 * question carries no `phases`, so fall back to whichever row container it has.
 * (The at-least-one rule counts the whole question instead; see `allRows`.)
 */
function currentPhaseRows(question) {
  var phases = question.phases;
  if (Array.isArray(phases) && phases.length) {
    var pos = question.phase_pos || 0;
    return Array.isArray(phases[pos]) ? phases[pos] : [];
  }
  if (Array.isArray(question.category_options)) {
    return question.category_options;
  }
  if (Array.isArray(question.response_options)) {
    return question.response_options;
  }
  return [];
}

/**
 * Every row in the question, across all pages. The at-least-one rule counts a
 * choice made anywhere, matching the legacy `getSelectedRowsCount`, which walked
 * every rendered row (hidden pages included). `response_options`/
 * `category_options` hold the full list; fall back to flattening `phases`.
 */
function allRows(question) {
  if (Array.isArray(question.category_options)) {
    return question.category_options;
  }
  if (Array.isArray(question.response_options)) {
    return question.response_options;
  }
  var phases = question.phases;
  if (Array.isArray(phases)) {
    return phases.reduce(function (all, chunk) {
      return Array.isArray(chunk) ? all.concat(chunk) : all;
    }, []);
  }
  return [];
}

/**
 * The answer variables a single row owns. Mirrors `../answers-adapter`
 * `variables.js`: a grid row names its cells in `response_inputs`, a checkbox
 * option names itself in `input_name`. A row with neither belongs to a
 * shared-variable question (radio/dropdown, one `question.input_id`) and is
 * reported as `null` so the caller counts that variable once, not per option.
 */
function rowVariables(question, row) {
  if (row && Array.isArray(row.response_inputs)) {
    return row.response_inputs;
  }
  if (row && typeof row.input_name === 'string') {
    return [row.input_name];
  }
  return null;
}

/**
 * Count answered rows in `rows`. The data-only port of `getSelectedRowsCount`
 * and the shared body of `selectedCount` (current page) and `selectedInQuestion`
 * (whole question). A row counts when, for a text grid (`input_type === 'text'`),
 * every input holds a value, otherwise any input does. A shared-variable
 * question declares no per-row variable, so its `question.input_id` is counted
 * once. Selection is presence only (see `isSelected`).
 */
function countSelected(question, answers, rows) {
  var isText = question.input_type === 'text';
  var perRow = rows.some(function (row) {
    return rowVariables(question, row) !== null;
  });

  if (!perRow) {
    // Radio or dropdown: one shared variable for the whole question.
    return isSelected(answers, question.input_id) ? 1 : 0;
  }

  var count = 0;
  for (var i = 0; i < rows.length; i++) {
    var names = rowVariables(question, rows[i]);
    if (!names || names.length === 0) {
      continue;
    }
    var answered = isText
      ? names.every(function (name) {
          return isSelected(answers, name);
        })
      : names.some(function (name) {
          return isSelected(answers, name);
        });
    if (answered) {
      count++;
    }
  }
  return count;
}

/**
 * How many rows on the current page count as answered.
 *
 * @param {Object} question The question object, read-only.
 * @param {Object} answers Snapshot keyed by variable name, read-only.
 * @returns {number} Count of answered rows on the current page.
 */
export function selectedCount(question, answers) {
  if (!question || typeof question !== 'object') {
    return 0;
  }
  return countSelected(question, answers || {}, currentPhaseRows(question));
}

/**
 * How many answered rows the whole question holds, across every page. Used by
 * the at-least-one rule; see `allRows`.
 */
function selectedInQuestion(question, answers) {
  if (!question || typeof question !== 'object') {
    return 0;
  }
  return countSelected(question, answers || {}, allRows(question));
}

/**
 * How many rows are shown on the current page. The data-only port of
 * `visible_rows_count` for the standard (non-transposed) layout. The transposed
 * grid divides by column count off the DOM and is not reproduced here; its
 * caller passes the result in as `opts.visibleCount`.
 *
 * @param {Object} question The question object, read-only.
 * @returns {number} Count of rows on the current page.
 */
export function visibleRowCount(question) {
  if (!question || typeof question !== 'object') {
    return 0;
  }
  return currentPhaseRows(question).length;
}

/**
 * Is the question's required rule satisfied on the current page? Ports both
 * legacy variants behind one option:
 *
 *   - default (grid, Base): `selectedCount >= visibleRowCount` — every visible
 *     row answered.
 *   - `opts.atLeastOne` (SingleMultiple): checked only on the last page, against
 *     "at least one row answered anywhere in the question" — the count spans
 *     every page, matching the legacy helper; earlier pages always pass so
 *     pagination continues.
 *
 * The gate is shared: a HARD question is always checked, a soft-required one
 * until its prompt has shown once (`opts.softPromptsShown`), anything else
 * passes. Counts come from `selectedCount`/`visibleRowCount`, or a caller that
 * still measures off the DOM (Matrix, Grid displaymax) injects
 * `opts.selectedCount`/`opts.visibleCount`.
 *
 * @param {Object} question Supplies `required`, `soft_required`, and (for
 *     `atLeastOne`) `phase_pos`/`phase_max`.
 * @param {Object} answers Snapshot keyed by variable name.
 * @param {Object} [opts] `atLeastOne`, `softPromptsShown`, `selectedCount`,
 *     `visibleCount`.
 * @returns {boolean} `true` when the respondent may proceed.
 */
export function isRequiredSatisfied(question, answers, opts) {
  opts = opts || {};
  question = question || {};

  var mustCheck =
    question.required === 'HARD' ||
    (question.soft_required && !opts.softPromptsShown);
  var injected = typeof opts.selectedCount === 'number';

  if (opts.atLeastOne) {
    // Earlier pages always pass; the check waits for the last page. Mirror the
    // legacy `phase_pos + 1 > phase_max` test, including its behavior when the
    // phase fields are absent (`NaN > undefined` is false, so not-last, passes).
    if (!(question.phase_pos + 1 > question.phase_max)) {
      return true;
    }
    if (!mustCheck) {
      return true;
    }
    // "At least one anywhere in the question": count across every page.
    var chosen = injected
      ? opts.selectedCount
      : selectedInQuestion(question, answers);
    return chosen >= 1;
  }

  if (!mustCheck) {
    return true;
  }
  var selected = injected
    ? opts.selectedCount
    : selectedCount(question, answers);
  var visible =
    typeof opts.visibleCount === 'number'
      ? opts.visibleCount
      : visibleRowCount(question);
  return selected >= visible;
}
